//* Libraries imports
import React, { useState } from "react";

//* Components imports
import { ResourceQuestionHeader } from "./resource-question-header";
import { ResourceQuestionTable } from "./index";
import { ResourceQuestionPagination } from "./resource-question-pagination";
import { DeleteConfirmationModal } from "../../../../../../component/common/DeleteConfirmationModal";

type ResourceQuestionData = {
  id: string;
  name: string;
  description?: string;
  status: "active" | "pending" | "inactive";
  deadline: {
    human: {
      date: string;
      time: string;
    };
  } | null;
  numberOfQuestions: number | string;
  createdAt: {
    human: {
      date: string;
      time: string;
    };
  };
}

type ResourceQuestionField = {
  name: string;
  header: string;
  type: "text" | "date" | "boolean" | "status";
}

type ResourceQuestionListProps = {
  title: string;
  pronoun?: string;
  searchPlaceholder?: string;
  notFoundMessage: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onCreate: () => void;
  onEditResourceQuestion?: (id: string) => void;
  onDeleteResourceQuestion?: (id: string) => void;
  onViewResourceQuestion?: (id: string) => void;
  items: ResourceQuestionData[];
  loading: boolean;
  hideEdit?: boolean;
  hideDelete?: boolean;
  fields: ResourceQuestionField[];
  additionalButtons?: Array<{
    label: string;
    eventHandler: (e: React.MouseEvent) => void;
  }>;

  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

export function ResourceQuestionList({
  title,
  pronoun = "os",
  searchPlaceholder = "Pesquisar...",
  notFoundMessage,
  searchTerm,
  onSearchChange,
  onSearch,
  onCreate,
  onEditResourceQuestion,
  onDeleteResourceQuestion,
  onViewResourceQuestion,
  items,
  loading,
  hideEdit = false,
  hideDelete = false,
  fields,
  additionalButtons = [],
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
}: ResourceQuestionListProps) {  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [selectedResourceName, setSelectedResourceName] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (id: string) => {
    const resource = items.find(item => item.id === id);
    setSelectedResourceId(id);
    setSelectedResourceName(resource?.name || "");
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedResourceId !== null && onDeleteResourceQuestion) {
      setIsDeleting(true);
      try {
        await onDeleteResourceQuestion(selectedResourceId);
      } finally {
        setIsDeleting(false);
        setShowDeleteModal(false);
        setSelectedResourceId(null);
        setSelectedResourceName("");
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedResourceId(null);
    setSelectedResourceName("");
  };
  return (
    <div className="container bg-white p-4 rounded-3 shadow-sm">

      {/* Header */}
      <ResourceQuestionHeader
        title={title}
        pronoun={pronoun}
        searchTerm={searchTerm}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={onSearchChange}
        onSearch={onSearch}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este recurso? Esta ação não pode ser desfeita."
        itemName={selectedResourceName}
        loading={isDeleting}
        confirmText="Excluir Recurso"
        cancelText="Cancelar"
      />


      {/* Table */}
      <div className="mt-4">
        <ResourceQuestionTable
          items={items}
          loading={loading}
          notFoundMessage={notFoundMessage}
          onEditResourceQuestion={onEditResourceQuestion}
          onDeleteResourceQuestion={handleDeleteClick}
          onViewResourceQuestion={onViewResourceQuestion}
          hideEdit={hideEdit}
          hideDelete={hideDelete}
          hideView={true}
          fields={fields}
        />
      </div>

      {/* Pagination */}
      {onPageChange && totalPages > 1 && (
        <ResourceQuestionPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
